import { listedDemoHostAuthority } from './demo-public-host';

type RuntimeLocationLike = {
  hostname?: string | null;
  port?: string | null;
  protocol?: string | null;
};

const DEFAULT_LOCAL_API_BASE_URL = 'http://127.0.0.1:4000';
const LOCAL_WEB_TO_API_PORT = new Map<string, string>([
  ['3100', '4000'],
  ['3101', '4000'],
  ['3200', '4100'],
  ['3410', '4310'],
]);
const DOCKER_INTERNAL_API_PORT = '4000';

function normalizeConfiguredBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function isLocalHost(hostname: string): boolean {
  return hostname === '127.0.0.1' || hostname === 'localhost';
}

function resolveLocalApiPort(webPort: string | null | undefined): string | null {
  const normalized = String(webPort ?? '').trim();
  if (!normalized) {
    return '4000';
  }
  const configuredPort = LOCAL_WEB_TO_API_PORT.get(normalized);
  if (configuredPort) {
    return configuredPort;
  }

  if (/^34\d{2}$/.test(normalized)) {
    return `43${normalized.slice(2)}`;
  }

  return null;
}

function readGlobalRuntimeLocation(): RuntimeLocationLike | null {
  if (typeof globalThis.location === 'undefined') {
    return null;
  }

  return {
    hostname: globalThis.location.hostname,
    port: globalThis.location.port,
    protocol: globalThis.location.protocol,
  };
}

export function resolveLocalRuntimeApiBaseUrl(runtimeLocation?: RuntimeLocationLike | null): string | null {
  const runtime = runtimeLocation ?? readGlobalRuntimeLocation();
  const hostname = String(runtime?.hostname ?? '').trim().toLowerCase();
  if (!isLocalHost(hostname)) {
    return null;
  }

  const apiPort = resolveLocalApiPort(runtime?.port);
  if (!apiPort) {
    return null;
  }

  const protocol = runtime?.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${hostname}:${apiPort}`;
}

export function resolveConfiguredOrLocalApiBaseUrl(
  configuredBaseUrl: string | null | undefined,
  options?: {
    runtimeLocation?: RuntimeLocationLike | null;
    nodeEnv?: string | null;
  },
): string {
  if (!options?.runtimeLocation && typeof window !== 'undefined'
    && listedDemoHostAuthority(window.location.hostname, process.env.NEXT_PUBLIC_HELIX_DEMO_PUBLIC_HOSTS))
    return '';
  const configured = String(configuredBaseUrl ?? '').trim();
  if (configured) {
    return normalizeConfiguredBaseUrl(configured);
  }

  const localRuntimeApiBaseUrl = resolveLocalRuntimeApiBaseUrl(options?.runtimeLocation);
  if (localRuntimeApiBaseUrl) {
    return localRuntimeApiBaseUrl;
  }

  const nodeEnv = options?.nodeEnv ?? process.env.NODE_ENV;
  if (nodeEnv === 'production') {
    return '';
  }

  return DEFAULT_LOCAL_API_BASE_URL;
}

export function resolveSessionCheckFallbackBaseUrls(apiBaseUrl: string): string[] {
  try {
    const parsed = new URL(apiBaseUrl);
    if (!isLocalHost(parsed.hostname.toLowerCase())) {
      return [];
    }

    const hostPort = parsed.port || (parsed.protocol === 'https:' ? '443' : '80');
    return [`http://api:${DOCKER_INTERNAL_API_PORT}`, `http://host.docker.internal:${hostPort}`];
  } catch {
    return [];
  }
}
